<template>
  <div class="login">
    <div class="login__bg"></div>
    <div class="login__content">
      <div class="login__brand">
        <div>
          <dwall class="dwall"/>
          <powered-by class="powered-by"/>
        </div>
        <div class="logo-square-wrapper">
          <login-logo-mobile class="logo-square"/>
        </div>
      </div>

      <div class="login__form">
        <login-logo-desktop class="logo-horizontal"/>
        <h1 class="h-title">{{ $t('resetPassword.title') }}</h1>
        <grid-row
          v-if="showError"
          column-align="center">
          <grid-row
            col-size="4">
            <h2>{{ $t('emailVerification.error') }}</h2>
          </grid-row>
        </grid-row>
        <grid-row
          v-if="showSuccess"
          column-align="center">
          <grid-row
            col-size="4">
            <h2>{{ $t('emailVerification.success') }}</h2>
          </grid-row>
        </grid-row>
        <p   v-if="!showSuccess" >{{ $t('resetPassword.subtitle') }} </p>

        <form
          v-if="!showSuccess"
          v-on:submit.prevent="doResetPasswordEmail"
          class="login-form"
          v-bind:class="{ 'form--inverse': darkTheme }">
          <label-input
            v-model="username"
            :error="loginErrorUserNotFound"
            :errorMessage="$t('login.userErrorMessage')"
            :label="$t('resetPassword.userLabel')"
            :placeholder="$t('login.userPlaceholder')">
          </label-input>
          <btn-pill
            type="submit"
            :btnText="$t('resetPassword.send')"
            btn-size="m">
            <font-awesome-icon :icon="check"/>
          </btn-pill>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import Dwall from '@/components/shared/logos/Dwall';
import PoweredBy from '@/components/shared/logos/PoweredBy';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import faCheck from '@fortawesome/fontawesome-free-solid/faCheck';
import LoginLogoMobile from '@/components/shared/logos/LoginLogoMobile';
import LoginLogoDesktop from '@/components/shared/logos/LoginLogoDesktop';
import variables from '../stylesheets/_variables.scss';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';

export default {
  name: 'ResetPasswordMail',
  components: {
    LabelInput,
    BtnPill,
    FontAwesomeIcon,
    Dwall,
    LoginLogoDesktop,
    PoweredBy,
    GridRow,
    GridCol,
    LoginLogoMobile
  },
  data () {
    return {
      username: '',
      label: '',
      darkTheme: variables.theme === '"dark"',
      showSuccess: false,
      showError: false
    };
  },
  computed: {
    ...mapGetters([
      'loginErrorUserNotFound'
    ]),
    check () {
      return faCheck;
    }
  },
  methods: {
    ...mapActions([
      'resetPasswordEmail'
    ]),
    doResetPasswordEmail () {
      this.resetPasswordEmail({context: this, username: this.username.toLowerCase()}).then(body => {
        this.handlerSuccess(body);
      }, error => {
        this.handlerError(error);
      });
    },
    handlerSuccess (body) {
      this.showSuccess = true;
      this.showError = false;
      if (body) {
        this.showSuccess = true;
      } else {
        this.showSuccess = false;
        this.showError = false;
      }
    },
    handlerError (error) {
      switch (error.status) {
        case 400:
          this.showSuccess = false;
          this.showError = true;
          break;

        default:
          this.showSuccess = false;
          this.showError = true;
          break;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../stylesheets/variables.scss";

.login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;

  &:after {
    content: "";
    background-size: auto 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    opacity: 1;
    @media (min-width: $screen-l) {
      background: no-repeat left center url(~BRAND/assets/login-bg.jpg);
      z-index: -3;
      background-position: left top;
      background-size: cover;
    }
  }

  &__bg {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    background-color: $login-background;
    @media (min-width: $screen-l) {
      background-color: transparent;
      background-image: radial-gradient(
          circle at 23% 35%,
          rgba($login-background, 0.5),
          rgba($login-background, 0.6),
          rgba($login-background, 0.9),
          $login-background 35%,
          $login-background
      );
    }
    @include brandCustom-loginBg();

    &:before {
      content: "";
      width: 100%;
      height: 100%;
      background-image: url($brandImage-LoginBgBefore);
      background-repeat: no-repeat;
      background-position: right bottom;
      position: absolute;
      right: 0;
      top: 0;
      opacity: 0.4;
      z-index: 0;
      @include brandCustom-loginBgBefore();
    }
  }

  &__content {
    color: $login-text-color;
    @media (max-width: $screen-l-max) {
      padding: 0 $sp-margin-large;
    }
    @media (min-width: $screen-l) {
      display: flex;
    }
  }

  &__brand {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: $sp-margin-larger;
    @media (max-width: $screen-l-max) {
      margin-top: $sp-margin-large;
      flex-direction: row;
      max-width: ($sp * 80) + $sp-margin-larger;
      padding-right: 0;
      margin-bottom: $sp-margin;
    }
  }

  .dwall {
    margin-bottom: $sp-margin;
    @media (max-width: $screen-l-max) {
      margin-bottom: $sp-margin-small;
      width: 80%;
      height: auto;
    }
  }

  .powered-by {
    width: 11rem;
    display: block;
    color: $login-powered-by-color;
    @media (max-width: $screen-l-max) {
      width: 90%;
      height: auto;
    }
  }

  &__form {
    max-width: ($sp * 80) + $sp-margin-larger;
    @media (min-width: $screen-l) {
      padding-left: $sp-margin-larger;
      border-left: 1px solid $login-form-right-border;
    }

    form {
      margin-bottom: $sp-margin;
    }
  }

  &__help {
    a {
      color: $link-dark;
      font-weight: bold;
    }
  }

  &__help-pass {
    font-weight: 700;

    a {
      color: $white;
    }
  }
}

.logo-horizontal {
  width: 230px;
  margin-bottom: $sp-margin;
  @media (max-width: $screen-l-max) {
    display: none;
  }
}

.logo-square-wrapper {
  width: 38%;
  flex-shrink: 0;
  padding-left: $sp-margin;
  border-left: 1px solid $white;
  display: flex;
  align-items: center;
  @media (min-width: $screen-l) {
    display: none;
  }
}

.logo-square {
  width: 100%;
  height: auto;
}
</style>



// WEBPACK FOOTER //
// src/layouts/ResetPasswordMail.vue